/**
 * Locale table for the IDE Design/Source surface — the `.fig` import panel at
 * `?studioDomain=design&studioView=sources`.
 *
 * WHY THIS EXISTS: the surface already called `useStudioLocale()` in two
 * places (the Review-menu trigger from `COOGO_IDE_CHROME_COPY`, and the
 * import-failure titles), while ~41 other creator-facing strings on the same
 * screen stayed English literals — including the three states a creator sees
 * first (empty / importing / blocked) and every status line the import,
 * export, evidence and revision paths write. Partly-wired is how the drift
 * happened; this table is the whole surface, not the next slice of it.
 *
 * Same co-located `Record<LocaleCode, …>` + accessor pattern as
 * `rail-surface-copy.ts` and `evidence-surface-copy.ts`.
 *
 * NOT LOCALIZED, deliberately — and asserted as such by the i18n gate:
 *   - file extensions and format tokens: `.fig`, `.pen`, `.docx`, `.hwpx`
 *   - product names: `Figma`, `Studio`, `Coogo`, `Design System Pack`
 *   - workspace decision-ledger `reason:` strings (`"Bulk token decision from
 *     Studio."`, `"Accepted Studio geometry suggestions."`). Those are audit
 *     payload written into the workspace record, not chrome — translating
 *     them would make the same decision read differently depending on who
 *     happened to be looking at the screen when it was taken.
 */
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

/**
 * Which of the three durability states an import is actually in. Kept as a
 * discriminated key rather than a boolean because a large import can be BOTH
 * unrestorable and rejected by localStorage.
 */
export type SourceDurabilityKind =
  | "restorable"
  | "browserOnly"
  | "notSaved"
  | "projectNotSaved";

export interface SourceSurfaceCopy {
  toolbar: {
    titleTokens: string;
    titleComponents: string;
    titleDesign: string;
    /** Shown when neither a design file nor a document names the screen. */
    subtitleFallback: string;
    importFig: string;
    importing: string;
    exportSource: string;
    exporting: string;
    exportBrowserFig: string;
    inspectSource: string;
    visualReview: string;
    editAppAndSource: string;
  };
  resources: {
    label: string;
    design: string;
    document: string;
    details: string;
  };
  states: {
    importingTitle: string;
    /** Also the first status line the import path writes. */
    preparingImport: string;
    retryChooseAnother: string;
    emptyTitle: string;
    emptyBody: string;
    emptyAction: string;
  };
  durability: Record<SourceDurabilityKind, string>;
  status: {
    designRead: (pages: number, layers: number) => string;
    exported: (fileName: string, checksumPrefix: string) => string;
    exportedChatNotice: (fileName: string) => string;
    exportFailed: string;
    /** `skipped === 0` drops the second clause entirely. */
    designSystemApplied: (projected: number, skipped: number) => string;
    evaluationOnlyEvidenceRecorded: string;
    sourceUseEvidenceRecorded: string;
    applyingBoundedEdits: string;
    comparingPreviews: string;
    browserRevisionVerified: (revision: number, surfaces: number) => string;
    /** Committed through the revision service with NO browser proof taken. */
    serverRevisionVerified: (revision: number, surfaces: number) => string;
    revisionFailed: string;
  };
  errors: {
    importBeforeRecordingEvidence: string;
    signInBeforeRecordingEvidence: string;
    projectChangedBeforeEvidence: string;
    evidenceNotPersisted: string;
    importOrRevisionRunning: string;
    importBeforeEditing: string;
    alreadyBoundToCandidate: string;
    projectChangedDuringRevision: string;
  };
}

const en: SourceSurfaceCopy = {
  toolbar: {
    titleTokens: "Tokens",
    titleComponents: "Components",
    titleDesign: "Design",
    subtitleFallback: "Import a .fig or document source",
    importFig: "Import .fig",
    importing: "Importing",
    exportSource: "Export source",
    exporting: "Exporting",
    exportBrowserFig: "Export browser .fig",
    inspectSource: "Inspect source",
    visualReview: "Visual review",
    editAppAndSource: "Edit app + source",
  },
  resources: {
    label: "Open resource",
    design: "Design",
    document: "Document",
    details: "Source details",
  },
  states: {
    importingTitle: "Reading your source file",
    preparingImport: "Preparing source import",
    retryChooseAnother: "Choose another file",
    emptyTitle: "Import a local Figma source",
    emptyBody:
      "Studio keeps your original file and what it read from it separate from anything generated, until review is complete.",
    emptyAction: "Choose .fig file",
  },
  durability: {
    restorable:
      "Saved to your account. This page's address reopens this import in another browser, signed in as you.",
    browserOnly:
      "Saved in this browser only. Another browser, a private window, or cleared site data will not have it.",
    notSaved:
      "This import is not saved anywhere. It was too large for this browser’s storage and it is not in your account, so leaving this page loses it. Sign in and import again to keep it.",
    projectNotSaved:
      "The source file is retained by your account, but this project could not save its restore reference. Retry before leaving this project.",
  },
  status: {
    designRead: (pages, layers) =>
      `Design read: ${pages} pages, ${layers} layers.`,
    exported: (fileName, checksumPrefix) =>
      `Exported ${fileName}; checksum ${checksumPrefix}... verified.`,
    exportedChatNotice: (fileName) =>
      `Exported the checksum-verified source: ${fileName}.`,
    exportFailed: "Could not export the retained source.",
    designSystemApplied: (projected, skipped) =>
      `Applied ${projected} token(s) to this project as its design system` +
      (skipped > 0 ? ` · ${skipped} accepted token(s) not projected` : "") +
      ". Preview generation only — this source is not promoted to a shared Design System Pack.",
    evaluationOnlyEvidenceRecorded:
      "Evaluation-only evidence recorded. Production use and Pack promotion remain disabled.",
    sourceUseEvidenceRecorded:
      "Source use evidence recorded. Rights review and a signed ledger are still required for Pack promotion.",
    applyingBoundedEdits: "Applying bounded edits to a new source revision",
    comparingPreviews: "Comparing revised source previews",
    browserRevisionVerified: (revision, surfaces) =>
      `Browser export/reopen verified without an extension. Revision ${revision} created; ${surfaces} surfaces require visual review.`,
    serverRevisionVerified: (revision, surfaces) =>
      `Revision ${revision} created through the source revision service. This page cannot run the browser .fig engine, so no browser export/reopen proof was taken; ${surfaces} surfaces require visual review.`,
    revisionFailed: "Could not create the source revision.",
  },
  errors: {
    importBeforeRecordingEvidence:
      "Import a .fig source before recording use evidence.",
    signInBeforeRecordingEvidence:
      "Sign in and save this project before recording source use evidence.",
    projectChangedBeforeEvidence:
      "The active project or source revision changed before evidence could be recorded.",
    evidenceNotPersisted: "Source use evidence was not persisted to the project.",
    importOrRevisionRunning:
      "Another source import or revision is already running.",
    importBeforeEditing: "Import a .fig source before editing it.",
    alreadyBoundToCandidate:
      "This source is already bound to a Coogo candidate. Start from an unbound source revision before editing it.",
    projectChangedDuringRevision:
      "The active project or source workspace changed during revision. The browser artifact was not promoted.",
  },
};

const ko: SourceSurfaceCopy = {
  toolbar: {
    titleTokens: "토큰",
    titleComponents: "컴포넌트",
    titleDesign: "디자인",
    subtitleFallback: ".fig 또는 문서 소스를 가져오세요",
    importFig: ".fig 가져오기",
    importing: "가져오는 중",
    exportSource: "소스 내보내기",
    exporting: "내보내는 중",
    exportBrowserFig: "브라우저 .fig 내보내기",
    inspectSource: "소스 살펴보기",
    visualReview: "시각 검토",
    editAppAndSource: "앱 + 소스 편집",
  },
  resources: {
    label: "열린 리소스",
    design: "디자인",
    document: "문서",
    details: "소스 상세",
  },
  states: {
    importingTitle: "소스 파일을 읽는 중입니다",
    preparingImport: "소스 가져오기를 준비하는 중",
    retryChooseAnother: "다른 파일 선택",
    emptyTitle: "로컬 Figma 소스 가져오기",
    emptyBody:
      "검토가 끝날 때까지 Studio는 원본 파일과 거기서 읽어낸 내용을, 생성된 결과물과 분리해서 보관합니다.",
    emptyAction: ".fig 파일 선택",
  },
  durability: {
    restorable:
      "계정에 저장되어 있습니다. 이 페이지 주소로 다른 브라우저에서도 같은 계정으로 로그인하면 이 임포트를 다시 열 수 있습니다.",
    browserOnly:
      "이 브라우저에만 저장됩니다. 다른 브라우저, 시크릿 창, 사이트 데이터 삭제 후에는 남아 있지 않습니다.",
    notSaved:
      "이 임포트는 어디에도 저장되지 않았습니다. 이 브라우저 저장소에 넣기에는 너무 크고 계정에도 없어서, 이 페이지를 떠나면 사라집니다. 로그인 후 다시 임포트하면 유지됩니다.",
    projectNotSaved:
      "소스 파일은 계정에 보관되어 있지만 이 프로젝트가 복원 참조를 저장하지 못했습니다. 프로젝트를 떠나기 전에 다시 시도하세요.",
  },
  status: {
    designRead: (pages, layers) =>
      `디자인을 읽었습니다: 페이지 ${pages}개, 레이어 ${layers}개.`,
    exported: (fileName, checksumPrefix) =>
      `${fileName} 내보내기 완료. 체크섬 ${checksumPrefix}... 확인됨.`,
    exportedChatNotice: (fileName) =>
      `체크섬이 확인된 소스를 내보냈습니다: ${fileName}.`,
    exportFailed: "보관된 소스를 내보내지 못했습니다.",
    designSystemApplied: (projected, skipped) =>
      `토큰 ${projected}개를 이 프로젝트의 디자인 시스템으로 적용했습니다` +
      (skipped > 0 ? ` · 승인된 토큰 ${skipped}개는 반영되지 않았습니다` : "") +
      ". 미리보기 생성 전용이며, 이 소스는 공유 Design System Pack으로 승격되지 않습니다.",
    evaluationOnlyEvidenceRecorded:
      "평가 전용 증적을 기록했습니다. 실사용과 Pack 승격은 계속 비활성 상태입니다.",
    sourceUseEvidenceRecorded:
      "소스 사용 증적을 기록했습니다. Pack 승격에는 권리 검토와 서명된 원장이 여전히 필요합니다.",
    applyingBoundedEdits: "제한된 편집을 새 소스 리비전에 적용하는 중",
    comparingPreviews: "수정된 소스 미리보기를 비교하는 중",
    browserRevisionVerified: (revision, surfaces) =>
      `확장 프로그램 없이 브라우저 내보내기/다시 열기를 확인했습니다. 리비전 ${revision}을(를) 만들었고, 화면 ${surfaces}개는 시각 검토가 필요합니다.`,
    serverRevisionVerified: (revision, surfaces) =>
      `소스 리비전 서비스로 리비전 ${revision}을(를) 만들었습니다. 이 페이지에서는 브라우저 .fig 엔진을 실행할 수 없어 내보내기/다시 열기 증명은 남기지 못했고, 화면 ${surfaces}개는 시각 검토가 필요합니다.`,
    revisionFailed: "소스 리비전을 만들지 못했습니다.",
  },
  errors: {
    importBeforeRecordingEvidence:
      "사용 증적을 기록하기 전에 .fig 소스를 먼저 가져오세요.",
    signInBeforeRecordingEvidence:
      "소스 사용 증적을 기록하려면 먼저 로그인하고 이 프로젝트를 저장하세요.",
    projectChangedBeforeEvidence:
      "증적이 기록되기 전에 활성 프로젝트 또는 소스 리비전이 바뀌었습니다.",
    evidenceNotPersisted:
      "소스 사용 증적이 프로젝트에 저장되지 않았습니다.",
    importOrRevisionRunning:
      "다른 소스 가져오기 또는 리비전 작업이 이미 실행 중입니다.",
    importBeforeEditing: "편집하기 전에 .fig 소스를 먼저 가져오세요.",
    alreadyBoundToCandidate:
      "이 소스는 이미 Coogo 후보에 연결되어 있습니다. 연결되지 않은 소스 리비전에서 시작한 뒤 편집하세요.",
    projectChangedDuringRevision:
      "리비전 도중 활성 프로젝트 또는 소스 워크스페이스가 바뀌었습니다. 브라우저 산출물은 승격되지 않았습니다.",
  },
};

const ja: SourceSurfaceCopy = {
  toolbar: {
    titleTokens: "トークン",
    titleComponents: "コンポーネント",
    titleDesign: "デザイン",
    subtitleFallback: ".fig またはドキュメントのソースを取り込んでください",
    importFig: ".fig を取り込む",
    importing: "取り込み中",
    exportSource: "ソースを書き出す",
    exporting: "書き出し中",
    exportBrowserFig: "ブラウザ .fig を書き出す",
    inspectSource: "ソースを確認",
    visualReview: "ビジュアル確認",
    editAppAndSource: "アプリ + ソースを編集",
  },
  resources: {
    label: "開いているリソース",
    design: "デザイン",
    document: "ドキュメント",
    details: "ソースの詳細",
  },
  states: {
    importingTitle: "ソースファイルを読み込んでいます",
    preparingImport: "ソースの取り込みを準備しています",
    retryChooseAnother: "別のファイルを選ぶ",
    emptyTitle: "ローカルの Figma ソースを取り込む",
    emptyBody:
      "確認が完了するまで、Studio は元のファイルとそこから読み取った内容を、生成物とは分けて保持します。",
    emptyAction: ".fig ファイルを選ぶ",
  },
  durability: {
    restorable:
      "アカウントに保存されています。このページのアドレスから、同じアカウントでログインすれば別のブラウザでもこのインポートを開けます。",
    browserOnly:
      "このブラウザにのみ保存されます。別のブラウザ、シークレットウィンドウ、サイトデータの削除後には残りません。",
    notSaved:
      "このインポートはどこにも保存されていません。このブラウザの保存領域には大きすぎ、アカウントにもないため、このページを離れると失われます。サインインして再度インポートしてください。",
    projectNotSaved:
      "ソースファイルはアカウントに保持されていますが、このプロジェクトは復元参照を保存できませんでした。プロジェクトを離れる前に再試行してください。",
  },
  status: {
    designRead: (pages, layers) =>
      `デザインを読み取りました: ${pages} ページ、${layers} レイヤー。`,
    exported: (fileName, checksumPrefix) =>
      `${fileName} を書き出しました。チェックサム ${checksumPrefix}... を確認済みです。`,
    exportedChatNotice: (fileName) =>
      `チェックサムを確認したソースを書き出しました: ${fileName}。`,
    exportFailed: "保持されたソースを書き出せませんでした。",
    designSystemApplied: (projected, skipped) =>
      `${projected} 個のトークンをこのプロジェクトのデザインシステムとして適用しました` +
      (skipped > 0 ? ` · 承認済みトークン ${skipped} 個は反映されていません` : "") +
      "。プレビュー生成のみで、このソースは共有 Design System Pack には昇格しません。",
    evaluationOnlyEvidenceRecorded:
      "評価目的のみの証跡を記録しました。本番利用と Pack への昇格は無効のままです。",
    sourceUseEvidenceRecorded:
      "ソース利用の証跡を記録しました。Pack への昇格には、権利確認と署名済み台帳が引き続き必要です。",
    applyingBoundedEdits: "限定的な編集を新しいソースリビジョンに適用しています",
    comparingPreviews: "修正後のソースプレビューを比較しています",
    browserRevisionVerified: (revision, surfaces) =>
      `拡張機能なしでブラウザの書き出し/再オープンを確認しました。リビジョン ${revision} を作成し、${surfaces} 個の画面にビジュアル確認が必要です。`,
    serverRevisionVerified: (revision, surfaces) =>
      `ソースリビジョンサービスでリビジョン ${revision} を作成しました。このページではブラウザの .fig エンジンを実行できないため、書き出し/再オープンの証跡は取得していません。${surfaces} 個の画面にビジュアル確認が必要です。`,
    revisionFailed: "ソースリビジョンを作成できませんでした。",
  },
  errors: {
    importBeforeRecordingEvidence:
      "利用証跡を記録する前に .fig ソースを取り込んでください。",
    signInBeforeRecordingEvidence:
      "ソース利用の証跡を記録するには、サインインしてこのプロジェクトを保存してください。",
    projectChangedBeforeEvidence:
      "証跡を記録する前に、対象のプロジェクトまたはソースリビジョンが変わりました。",
    evidenceNotPersisted:
      "ソース利用の証跡がプロジェクトに保存されませんでした。",
    importOrRevisionRunning:
      "別のソース取り込みまたはリビジョン処理がすでに実行中です。",
    importBeforeEditing: "編集する前に .fig ソースを取り込んでください。",
    alreadyBoundToCandidate:
      "このソースはすでに Coogo の候補に結び付けられています。結び付いていないソースリビジョンから始めてください。",
    projectChangedDuringRevision:
      "リビジョン処理中に、対象のプロジェクトまたはソースワークスペースが変わりました。ブラウザの成果物は昇格されていません。",
  },
};

const zh: SourceSurfaceCopy = {
  toolbar: {
    titleTokens: "令牌",
    titleComponents: "组件",
    titleDesign: "设计",
    subtitleFallback: "导入 .fig 或文档来源",
    importFig: "导入 .fig",
    importing: "正在导入",
    exportSource: "导出来源",
    exporting: "正在导出",
    exportBrowserFig: "导出浏览器 .fig",
    inspectSource: "检查来源",
    visualReview: "视觉审阅",
    editAppAndSource: "编辑应用 + 来源",
  },
  resources: {
    label: "打开的资源",
    design: "设计",
    document: "文档",
    details: "来源详情",
  },
  states: {
    importingTitle: "正在读取你的来源文件",
    preparingImport: "正在准备导入来源",
    retryChooseAnother: "选择其他文件",
    emptyTitle: "导入本地 Figma 来源",
    emptyBody:
      "在审阅完成之前，Studio 会把你的原始文件及从中读取的内容，与任何生成结果分开保存。",
    emptyAction: "选择 .fig 文件",
  },
  durability: {
    restorable:
      "已保存到您的账户。使用同一账户登录后，可通过此页面地址在其他浏览器中重新打开该导入。",
    browserOnly:
      "仅保存在此浏览器中。换用其他浏览器、隐私窗口或清除站点数据后将不再存在。",
    notSaved:
      "此导入尚未保存到任何地方。它超出了此浏览器的存储上限，也不在您的账户中，离开此页面即会丢失。请登录后重新导入。",
    projectNotSaved:
      "源文件已保留在您的账户中，但此项目未能保存恢复引用。请在离开项目之前重试。",
  },
  status: {
    designRead: (pages, layers) =>
      `已读取设计：${pages} 个页面，${layers} 个图层。`,
    exported: (fileName, checksumPrefix) =>
      `已导出 ${fileName}；校验和 ${checksumPrefix}... 已验证。`,
    exportedChatNotice: (fileName) =>
      `已导出通过校验和验证的来源：${fileName}。`,
    exportFailed: "无法导出已保留的来源。",
    designSystemApplied: (projected, skipped) =>
      `已将 ${projected} 个令牌作为此项目的设计系统应用` +
      (skipped > 0 ? ` · 有 ${skipped} 个已接受的令牌未被映射` : "") +
      "。仅用于预览生成，此来源不会晋级为共享的 Design System Pack。",
    evaluationOnlyEvidenceRecorded:
      "已记录仅供评估的证据。生产使用与 Pack 晋级仍处于停用状态。",
    sourceUseEvidenceRecorded:
      "已记录来源使用证据。Pack 晋级仍需权利审查与已签名的台账。",
    applyingBoundedEdits: "正在将受限编辑应用到新的来源修订",
    comparingPreviews: "正在比较修订后的来源预览",
    browserRevisionVerified: (revision, surfaces) =>
      `已在没有扩展程序的情况下验证浏览器导出/重新打开。已创建修订 ${revision}；有 ${surfaces} 个画面需要视觉审阅。`,
    serverRevisionVerified: (revision, surfaces) =>
      `已通过来源修订服务创建修订 ${revision}。此页面无法运行浏览器 .fig 引擎，因此未取得导出/重新打开的证明；有 ${surfaces} 个画面需要视觉审阅。`,
    revisionFailed: "无法创建来源修订。",
  },
  errors: {
    importBeforeRecordingEvidence: "记录使用证据前，请先导入 .fig 来源。",
    signInBeforeRecordingEvidence:
      "记录来源使用证据前，请先登录并保存此项目。",
    projectChangedBeforeEvidence:
      "证据尚未记录，当前项目或来源修订已发生变化。",
    evidenceNotPersisted: "来源使用证据未保存到项目中。",
    importOrRevisionRunning: "另一个来源导入或修订操作正在进行中。",
    importBeforeEditing: "编辑前请先导入 .fig 来源。",
    alreadyBoundToCandidate:
      "此来源已绑定到一个 Coogo 候选。请先从未绑定的来源修订开始，再进行编辑。",
    projectChangedDuringRevision:
      "修订过程中当前项目或来源工作区发生了变化，浏览器产物未被晋级。",
  },
};

const zhTW: SourceSurfaceCopy = {
  toolbar: {
    titleTokens: "權杖",
    titleComponents: "元件",
    titleDesign: "設計",
    subtitleFallback: "匯入 .fig 或文件來源",
    importFig: "匯入 .fig",
    importing: "正在匯入",
    exportSource: "匯出來源",
    exporting: "正在匯出",
    exportBrowserFig: "匯出瀏覽器 .fig",
    inspectSource: "檢查來源",
    visualReview: "視覺審閱",
    editAppAndSource: "編輯應用 + 來源",
  },
  resources: {
    label: "開啟的資源",
    design: "設計",
    document: "文件",
    details: "來源詳細資訊",
  },
  states: {
    importingTitle: "正在讀取你的來源檔案",
    preparingImport: "正在準備匯入來源",
    retryChooseAnother: "選擇其他檔案",
    emptyTitle: "匯入本機 Figma 來源",
    emptyBody:
      "在審閱完成之前，Studio 會把你的原始檔案及從中讀取的內容，與任何生成結果分開保存。",
    emptyAction: "選擇 .fig 檔案",
  },
  durability: {
    restorable:
      "已儲存至您的帳戶。使用同一帳戶登入後，可透過此頁面網址在其他瀏覽器重新開啟此匯入。",
    browserOnly:
      "僅儲存在此瀏覽器中。改用其他瀏覽器、無痕視窗或清除網站資料後將不再存在。",
    notSaved:
      "此匯入尚未儲存到任何地方。它超出此瀏覽器的儲存上限，也不在您的帳戶中，離開此頁面即會遺失。請登入後重新匯入。",
    projectNotSaved:
      "來源檔案已保留在您的帳戶中，但此專案無法儲存還原參照。請在離開專案前重試。",
  },
  status: {
    designRead: (pages, layers) =>
      `已讀取設計：${pages} 個頁面，${layers} 個圖層。`,
    exported: (fileName, checksumPrefix) =>
      `已匯出 ${fileName}；總和檢查碼 ${checksumPrefix}... 已驗證。`,
    exportedChatNotice: (fileName) =>
      `已匯出通過總和檢查碼驗證的來源：${fileName}。`,
    exportFailed: "無法匯出已保留的來源。",
    designSystemApplied: (projected, skipped) =>
      `已將 ${projected} 個權杖作為此專案的設計系統套用` +
      (skipped > 0 ? ` · 有 ${skipped} 個已接受的權杖未被對應` : "") +
      "。僅用於預覽生成，此來源不會晉級為共用的 Design System Pack。",
    evaluationOnlyEvidenceRecorded:
      "已記錄僅供評估的證據。正式使用與 Pack 晉級仍為停用狀態。",
    sourceUseEvidenceRecorded:
      "已記錄來源使用證據。Pack 晉級仍需權利審查與已簽署的帳本。",
    applyingBoundedEdits: "正在將受限編輯套用到新的來源修訂",
    comparingPreviews: "正在比較修訂後的來源預覽",
    browserRevisionVerified: (revision, surfaces) =>
      `已在沒有擴充功能的情況下驗證瀏覽器匯出/重新開啟。已建立修訂 ${revision}；有 ${surfaces} 個畫面需要視覺審閱。`,
    serverRevisionVerified: (revision, surfaces) =>
      `已透過來源修訂服務建立修訂 ${revision}。此頁面無法執行瀏覽器 .fig 引擎，因此未取得匯出/重新開啟的證明；有 ${surfaces} 個畫面需要視覺審閱。`,
    revisionFailed: "無法建立來源修訂。",
  },
  errors: {
    importBeforeRecordingEvidence: "記錄使用證據前，請先匯入 .fig 來源。",
    signInBeforeRecordingEvidence:
      "記錄來源使用證據前，請先登入並儲存此專案。",
    projectChangedBeforeEvidence:
      "證據尚未記錄，目前專案或來源修訂已變更。",
    evidenceNotPersisted: "來源使用證據未儲存到專案中。",
    importOrRevisionRunning: "另一個來源匯入或修訂作業正在進行中。",
    importBeforeEditing: "編輯前請先匯入 .fig 來源。",
    alreadyBoundToCandidate:
      "此來源已綁定到一個 Coogo 候選。請先從未綁定的來源修訂開始，再進行編輯。",
    projectChangedDuringRevision:
      "修訂過程中目前專案或來源工作區已變更，瀏覽器產物未被晉級。",
  },
};

const SOURCE_SURFACE_COPY: Record<LocaleCode, SourceSurfaceCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getSourceSurfaceCopy(locale: string): SourceSurfaceCopy {
  return SOURCE_SURFACE_COPY[locale as LocaleCode] ?? en;
}
